import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Grid from '@mui/material/Grid2'
import React from 'react'
import EditButton from '../IconButtons/EditButton'

/**
 * The extra props this HOC adds vary in shape depending on the wrapped component, so
 * they are modelled as a loose record with the fields this file itself relies on made
 * explicit.
 */
type ProfileCardItemProps = {
  profileWarningId?: string
  showDivider?: boolean
  editable?: boolean
  onOpen?: () => void
  header?: string
  [key: string]: unknown
}

/**
 * Profile card item higher order component.
 *
 * @param Component - The component to wrap.
 * @returns The wrapped component.
 * @notExported
 */
const withProfileCardItem = <TComponentProps extends object>(Component: React.ComponentType<TComponentProps>) => {
  const ProfileCardItem = (props: ProfileCardItemProps) => {
    const theme = useTheme()
    const mobileView = useMediaQuery(theme.breakpoints.down('md'))

    return (
      <>
        <Grid
          id={props.profileWarningId}
          tabIndex={props.profileWarningId ? -1 : undefined}
          sx={
            props.showDivider !== undefined
              ? {
                  width: '100%',
                  background: theme.palette.background.default,
                  padding: '0px',
                  margin: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  borderRadius: '0px',
                  boxShadow: 'none',
                }
              : {
                  width: '100%',
                  background: theme.palette.background.default,
                  borderRadius: '5px',
                  padding: '0px',
                  margin: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  marginTop: '18px',
                  [theme.breakpoints.down('sm')]: {
                    marginTop: '10px',
                  },
                }
          }
        >
          <Grid
            sx={{
              width: '95%',
              [theme.breakpoints.down('sm')]: {
                margin: '8px 8px',
              },
              margin: '18px 18px',
              alignItems: 'start',
            }}
          >
            <Grid container justifyContent="space-between">
              <Grid size={11}>
                {React.createElement(Component, props as unknown as React.Attributes & TComponentProps)}
              </Grid>
              <Grid size={1}>
                {props.editable && (
                  <EditButton
                    testId={`button-profile-card-item-edit-${props.header}`}
                    clickAction={() => props.onOpen?.()}
                    size={mobileView ? 'small' : 'medium'}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {props.showDivider && (
          <Grid
            sx={{
              width: '95%',
              [theme.breakpoints.down('sm')]: {
                margin: '8px 8px',
              },
              margin: '18px 18px',
              height: '1px',
              background: theme.palette.neutral.main,
            }}
          />
        )}
      </>
    )
  }
  return ProfileCardItem
}

export default withProfileCardItem
